import React, { useState } from 'react';
import PetIllustration from './PetIllustration';
import type { Pet } from '../types';
import '../styles/PetPhoto.css';

type PetPhotoVariant = 'card' | 'hero' | 'thumb';

const SIZING: Record<PetPhotoVariant, { w: number; h: number }> = {
  // Roughly 2× the displayed CSS box, capped to keep payloads tight.
  card: { w: 600, h: 800 },
  hero: { w: 1200, h: 1500 },
  thumb: { w: 200, h: 200 },
};

/**
 * Unsplash supports query-based image transforms. When the photoUrl points
 * at images.unsplash.com we ask for a sensibly sized, modern-format variant
 * so cards don't pull multi-MB originals. Other hosts (Firebase Storage)
 * don't transform — return the URL untouched.
 */
function decorateUnsplash(url: string, w: number, h: number): string {
  if (!url.includes('images.unsplash.com')) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('w', String(w));
    u.searchParams.set('h', String(h));
    u.searchParams.set('fit', 'crop');
    u.searchParams.set('auto', 'format');
    u.searchParams.set('q', '75');
    return u.toString();
  } catch {
    return url;
  }
}

interface PetPhotoProps {
  pet: Pet;
  /** Override the photoUrl on the pet (e.g. detail-page gallery selection). */
  photoUrl?: string;
  /** Used for the alt text and as a hint to lazy-load when off-screen. */
  loading?: 'lazy' | 'eager';
  /** Picks intrinsic dimensions + the size requested from image CDNs. */
  variant?: PetPhotoVariant;
  className?: string;
}

const PetPhoto: React.FC<PetPhotoProps> = ({
  pet,
  photoUrl,
  loading = 'lazy',
  variant = 'card',
  className,
}) => {
  // Derive the source from props on every render so that prop changes
  // (e.g. detail-page gallery selection) actually swap the rendered image.
  const src = photoUrl ?? pet.photoUrl;
  const [prevSrc, setPrevSrc] = useState(src);
  const [broken, setBroken] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Reset error + loaded state when the source URL changes. We do this
  // during render (not in an effect) so the reset happens before commit
  // — otherwise a cached image's onLoad can fire before the effect runs
  // and the effect's setLoaded(false) leaves the placeholder stuck on.
  if (src !== prevSrc) {
    setPrevSrc(src);
    setBroken(false);
    setLoaded(false);
  }

  if (!src || broken) {
    return (
      <PetIllustration
        kind={pet.illustration.kind}
        variant={pet.illustration.variant}
        palette={pet.illustration.palette}
        className={className}
      />
    );
  }

  const { w, h } = SIZING[variant];
  const optimized = decorateUnsplash(src, w, h);

  return (
    <div className={`pet-photo-wrap ${loaded ? 'is-loaded' : ''} ${className || ''}`}>
      {!loaded && (
        <img
          className="pet-photo__placeholder"
          src={`${process.env.PUBLIC_URL}/animation.gif`}
          alt=""
          aria-hidden="true"
          decoding="async"
        />
      )}
      <img
        className="pet-photo"
        src={optimized}
        width={w}
        height={h}
        alt={pet.name}
        loading={loading}
        decoding="async"
        fetchPriority={loading === 'eager' ? 'high' : undefined}
        onLoad={() => setLoaded(true)}
        onError={() => setBroken(true)}
      />
    </div>
  );
};

export default PetPhoto;
